import { combine, createEvent, createStore, sample } from "effector"
import { or, reset } from "patronum"
import { $callcenterService } from "@callcenter/effector-services"
import { createToggle } from "@callcenter/effector-helpers"

import { selfServiceApi } from "../api"
import { TOTAL_TRIAL_DAYS } from "../shared/constants"

const upgradeNowFromMessage = (event: MessageEvent) => {
  const data = event.data as { source: string; isActiveUpgradeNow?: boolean }

  if (/^react-devtools/gi.test(data.source) || !data.isActiveUpgradeNow) {
    return
  }

  upgradeNowClicked()
}

export const $isUpgradeSent = createStore(
  // eslint-disable-next-line effector/no-getState
  Boolean($callcenterService.$settings.getState().self_service_trial_upgrade_requested)
).on($callcenterService.$settings, (_, settings) => Boolean(settings.self_service_trial_upgrade_requested))

export const [$isUpgradeNowModalShown, _, upgradeNowClicked, closeUpgradeNowModal] = createToggle(false)

export const [$isVerificationModalShown, ___, verificationModalOpened, closeVerificationModal] = createToggle(false)

export const [$isUnderReview, __, underReviewOpened, closeUnderReviewModal] = createToggle(false)

const $isKycAccepted = $callcenterService.$settings.map((state) => state.kyc?.status === "accepted")
const $isKycDeclined = $callcenterService.$settings.map((state) => state.kyc?.status === "declined")

export const $isUpgradeDisabled = combine(
  $isKycAccepted,
  $isKycDeclined,
  $isUnderReview,
  (accepted, declined, underReview) => accepted || declined || underReview
)

export const $isSelfServiceMode = $callcenterService.$settings.map((state) => Boolean(state.self_service_trial))
export const $isTrialExpired = $callcenterService.$settings.map(
  (state) =>
    Boolean(state.self_service_trial_expired) ||
    ("self_service_trial_days_remaining" in state && state.self_service_trial_days_remaining! < 1)
)
export const $trialDaysRemaining = $callcenterService.$settings.map(
  (state) => state.self_service_trial_days_remaining ?? 0
)

export const $isActiveTrial = combine(
  $isSelfServiceMode,
  $isTrialExpired,
  (isSelfServiceMode, isTrialExpired) => isSelfServiceMode && !isTrialExpired
)

export const $webchatTrialSettings = combine({
  trialDaysRemaining: $trialDaysRemaining,
  isUpgradeDisabled: $isUpgradeDisabled
}).map(({ trialDaysRemaining, isUpgradeDisabled }) => ({
  days: { left: TOTAL_TRIAL_DAYS - trialDaysRemaining, total: TOTAL_TRIAL_DAYS },
  shouldOpenTrialSupportChat: false,
  shouldOpenTrialHome: false,
  shouldOpenTrialPreview: false,
  disableUpgradeButton: isUpgradeDisabled
}))
export const $setupSettings = combine({
  webchatTrialSettings: $webchatTrialSettings,
  isSelfServiceMode: $isSelfServiceMode
})

export const $isLoading = or(selfServiceApi.requestUpgradeFx.pending, selfServiceApi.startKycFx.pending)

export const requestUpgrade = createEvent()
export const tryAgainClicked = createEvent()
export const contactUsClicked = createEvent()
export const startVerificationClicked = createEvent()
export const verifyOnMobileClicked = createEvent()

export const [$isQrModalShown, _toggleQr, qrModalOpened, qrModalClosed] = createToggle(false)
export const $mobileVerificationUrl = createStore<string | null>(null)

reset({ clock: qrModalClosed, target: $mobileVerificationUrl })

const $verificationPath = createStore<"desktop" | "mobile" | null>(null)

sample({ clock: [startVerificationClicked, tryAgainClicked], fn: () => "desktop" as const, target: $verificationPath })
sample({ clock: verifyOnMobileClicked, fn: () => "mobile" as const, target: $verificationPath })

export const $isDesktopVerifying = combine(
  $isLoading,
  $verificationPath,
  (isLoading, path) => isLoading && path === "desktop"
)
export const $isMobileVerifying = combine(
  $isLoading,
  $verificationPath,
  (isLoading, path) => isLoading && path === "mobile"
)

export const [$isEmailModalShown, _toggleEmail, emailModalOpened, emailModalClosed] = createToggle(false)
export const sendEmailConfirmed = createEvent()
export const $isEmailSent = createStore(false).reset(closeVerificationModal)
export const $isEmailSending = selfServiceApi.sendVerificationEmailFx.pending

$isSelfServiceMode.watch((isSelfServiceMode) => {
  window.removeEventListener("message", upgradeNowFromMessage)

  if (isSelfServiceMode) {
    window.addEventListener("message", upgradeNowFromMessage)

    /* eslint-disable-next-line effector/no-getState */
    selfServiceApi.startSetupFx($webchatTrialSettings.getState())
  }
})

sample({
  clock: requestUpgrade,
  target: [closeUpgradeNowModal, verificationModalOpened]
})

// First upgrade goes through /upgrade_now; once the upgrade is already requested every
// later attempt starts a fresh KYC session via startKycFx, which the backend allows after
// a cancelled/timeout result and which avoids the 422 that re-requesting the upgrade returns.
sample({
  clock: [startVerificationClicked, tryAgainClicked, verifyOnMobileClicked],
  source: $isUpgradeSent,
  filter: (isUpgradeSent) => !isUpgradeSent,
  target: selfServiceApi.requestUpgradeFx
})

sample({
  clock: [startVerificationClicked, tryAgainClicked, verifyOnMobileClicked],
  source: $isUpgradeSent,
  filter: (isUpgradeSent) => isUpgradeSent,
  target: selfServiceApi.startKycFx
})

sample({
  clock: [selfServiceApi.requestUpgradeFx.doneData, selfServiceApi.startKycFx.doneData],
  source: $verificationPath,
  filter: (path) => path !== "mobile",
  fn: (_path, { verification_url }) => verification_url,
  target: selfServiceApi.redirectToKycFx
})

sample({
  clock: [selfServiceApi.requestUpgradeFx.doneData, selfServiceApi.startKycFx.doneData],
  source: $verificationPath,
  filter: (path) => path === "mobile",
  fn: (_path, { verification_url }) => verification_url,
  target: [$mobileVerificationUrl, qrModalOpened]
})

sample({
  clock: sendEmailConfirmed,
  target: selfServiceApi.sendVerificationEmailFx
})

sample({
  clock: selfServiceApi.sendVerificationEmailFx.done,
  target: emailModalClosed
})

sample({
  clock: selfServiceApi.sendVerificationEmailFx.done,
  fn: () => true,
  target: $isEmailSent
})

sample({
  clock: selfServiceApi.requestUpgradeFx.done,
  fn: () => true,
  target: $isUpgradeSent
})

sample({
  clock: selfServiceApi.requestUpgradeFx.done,
  target: closeUnderReviewModal
})

sample({
  clock: contactUsClicked,
  source: $setupSettings,
  filter: ({ isSelfServiceMode }) => isSelfServiceMode,
  fn: ({ webchatTrialSettings }) => ({
    ...webchatTrialSettings,
    shouldOpenTrialSupportChat: true
  }),
  target: selfServiceApi.startSetupFx
})

sample({
  clock: $isUpgradeDisabled,
  source: $setupSettings,
  filter: ({ isSelfServiceMode }) => isSelfServiceMode,
  fn: ({ webchatTrialSettings }) => webchatTrialSettings,
  target: selfServiceApi.startSetupFx
})
